import { useState, useEffect, useRef, useCallback } from 'react';
import { useApp } from '@context/AppContext';
import { Play, Pause, RotateCcw, X, SkipForward, SkipBack, Video, Loader2 } from 'lucide-react';

export default function ReplayPanel({ onClose, stageRef }) {
  const { state, dispatch } = useApp();
  const { canvas, room } = state;
  const [playing, setPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [recording, setRecording] = useState(false);
  const [recordingProgress, setRecordingProgress] = useState('');
  const timerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recordingResolveRef = useRef(null);

  // Store original strokes on mount — this is the source of truth for replay
  const originalStrokesRef = useRef(null);
  const [sortedStrokes, setSortedStrokes] = useState([]);
  const total = sortedStrokes.length;

  useEffect(() => {
    // Capture strokes at the moment replay opens
    const originals = [...canvas.allStrokes];
    originalStrokesRef.current = originals;
    setSortedStrokes(originals.sort((a, b) => a.timestamp - b.timestamp));
    setCurrentIndex(0);
    // Clear canvas for replay start
    dispatch({ type: 'SET_REPLAY_STROKES', payload: [] });
    return () => {
      // Restore original strokes when replay closes
      if (originalStrokesRef.current) {
        dispatch({ type: 'RESTORE_STROKES', payload: originalStrokesRef.current });
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Play/pause timer
  useEffect(() => {
    if (!playing || currentIndex >= total) {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (currentIndex >= total) {
        setPlaying(false);
        // If recording, stop the recorder
        if (recording && mediaRecorderRef.current?.state === 'recording') {
          mediaRecorderRef.current.stop();
        }
      }
      return;
    }

    // Calculate delay based on actual timestamp gaps, capped
    const getDelay = () => {
      if (currentIndex >= total - 1) return 200;
      const gap = sortedStrokes[currentIndex + 1].timestamp - sortedStrokes[currentIndex].timestamp;
      return Math.min(Math.max(gap / speed, 16), 500 / speed);
    };

    timerRef.current = setTimeout(() => {
      setCurrentIndex((i) => i + 1);
      if (recording) {
        setRecordingProgress(`Recording: ${currentIndex + 1}/${total} strokes`);
      }
    }, getDelay());

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [playing, currentIndex, total, speed, sortedStrokes, recording]);

  // Update the visible strokes in the canvas state
  useEffect(() => {
    const visibleStrokes = sortedStrokes.slice(0, currentIndex);
    dispatch({ type: 'SET_REPLAY_STROKES', payload: visibleStrokes });
  }, [currentIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReset = () => {
    setPlaying(false);
    setCurrentIndex(0);
  };

  const handleClose = () => {
    if (originalStrokesRef.current) {
      dispatch({ type: 'RESTORE_STROKES', payload: originalStrokesRef.current });
    }
    onClose();
  };

  // Export replay as video
  const handleExportVideo = useCallback(async () => {
    const stage = stageRef?.current;
    if (!stage || total === 0) return;

    // Create an offscreen canvas to composite Konva frames onto
    const offscreen = document.createElement('canvas');
    const stageWidth = stage.width();
    const stageHeight = stage.height();
    offscreen.width = stageWidth;
    offscreen.height = stageHeight;
    const ctx = offscreen.getContext('2d');

    // Check MediaRecorder support
    if (!window.MediaRecorder) {
      dispatch({ type: 'ADD_TOAST', payload: { message: 'Video recording not supported in this browser' } });
      return;
    }

    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
      ? 'video/webm;codecs=vp9'
      : MediaRecorder.isTypeSupported('video/webm')
        ? 'video/webm'
        : MediaRecorder.isTypeSupported('video/mp4')
          ? 'video/mp4'
          : null;

    if (!mimeType) {
      dispatch({ type: 'ADD_TOAST', payload: { message: 'No supported video format found' } });
      return;
    }

    setRecording(true);
    setRecordingProgress('Preparing...');

    // Set up MediaRecorder on the offscreen canvas stream
    const stream = offscreen.captureStream(30);
    const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 5000000 });
    chunksRef.current = [];
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      const ext = mimeType.includes('mp4') ? 'mp4' : 'webm';
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `drawlab-replay-${room.code}-${Date.now()}.${ext}`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
      setRecording(false);
      setRecordingProgress('');
      dispatch({ type: 'ADD_TOAST', payload: { message: 'Replay video saved!' } });
    };

    recorder.onerror = () => {
      setRecording(false);
      setRecordingProgress('');
      dispatch({ type: 'ADD_TOAST', payload: { message: 'Recording failed' } });
    };

    recorder.start(100);

    // Play through all strokes, rendering each frame to the offscreen canvas
    let frameIdx = 0;
    dispatch({ type: 'SET_REPLAY_STROKES', payload: [] });
    setCurrentIndex(0);

    await new Promise((r) => setTimeout(r, 200));

    const renderFrame = () => {
      if (frameIdx > total) {
        // Done — stop recording
        recorder.stop();
        return;
      }

      // Update strokes
      const visible = sortedStrokes.slice(0, frameIdx);
      dispatch({ type: 'SET_REPLAY_STROKES', payload: visible });
      setCurrentIndex(frameIdx);
      setRecordingProgress(`Recording: ${frameIdx}/${total}`);

      // Render Konva stage to the offscreen canvas
      requestAnimationFrame(() => {
        try {
          const dataUrl = stage.toDataURL({ pixelRatio: 1 });
          const img = new window.Image();
          img.onload = () => {
            ctx.clearRect(0, 0, stageWidth, stageHeight);
            ctx.drawImage(img, 0, 0, stageWidth, stageHeight);

            frameIdx++;
            // Delay based on timestamp gaps
            let delay = 50;
            if (frameIdx < total) {
              const gap = sortedStrokes[frameIdx].timestamp - sortedStrokes[frameIdx - 1].timestamp;
              delay = Math.min(Math.max(gap / speed, 16), 500 / speed);
            }
            setTimeout(renderFrame, delay);
          };
          img.src = dataUrl;
        } catch {
          frameIdx++;
          setTimeout(renderFrame, 30);
        }
      });
    };

    // Start rendering frames
    setTimeout(renderFrame, 100);
  }, [stageRef, total, sortedStrokes, speed, dispatch, room.code]);

  return (
    <div className="absolute bottom-14 left-1/2 -translate-x-1/2 z-20 bg-charcoal/90 backdrop-blur rounded-2xl shadow-xl px-4 py-3 flex items-center gap-3">
      {/* Reset */}
      <button
        onClick={handleReset}
        disabled={recording}
        title="Reset"
        className="w-8 h-8 flex items-center justify-center rounded-lg text-white/60 hover:text-white transition-colors disabled:opacity-30"
      >
        <RotateCcw className="w-4 h-4" />
      </button>

      {/* Step back */}
      <button
        onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
        disabled={recording}
        title="Step back"
        className="w-8 h-8 flex items-center justify-center rounded-lg text-white/60 hover:text-white transition-colors disabled:opacity-30"
      >
        <SkipBack className="w-4 h-4" />
      </button>

      {/* Play/Pause */}
      <button
        onClick={() => {
          if (recording) return;
          if (currentIndex >= total) setCurrentIndex(0);
          setPlaying(!playing);
        }}
        disabled={recording}
        className="w-10 h-10 flex items-center justify-center rounded-full bg-amber text-white hover:bg-amber-dark transition-colors disabled:opacity-50"
      >
        {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
      </button>

      {/* Step forward */}
      <button
        onClick={() => setCurrentIndex((i) => Math.min(total, i + 1))}
        disabled={recording}
        title="Step forward"
        className="w-8 h-8 flex items-center justify-center rounded-lg text-white/60 hover:text-white transition-colors disabled:opacity-30"
      >
        <SkipForward className="w-4 h-4" />
      </button>

      {/* Progress */}
      <div className="flex items-center gap-2">
        <input
          type="range"
          min={0}
          max={total}
          value={currentIndex}
          onChange={(e) => {
            if (recording) return;
            setCurrentIndex(Number(e.target.value));
            setPlaying(false);
          }}
          disabled={recording}
          className="w-32 h-1 accent-amber appearance-none bg-white/20 rounded cursor-pointer disabled:opacity-30"
        />
        <span className="text-xs text-white/60 font-mono min-w-[4rem] text-right">
          {currentIndex}/{total}
        </span>
      </div>

      {/* Speed controls */}
      <div className="flex items-center gap-1 ml-1">
        {[1, 2, 4, 8, 16, 50].map((s) => (
          <button
            key={s}
            onClick={() => { if (!recording) setSpeed(s); }}
            disabled={recording}
            className={`px-2 h-6 text-[10px] font-mono rounded transition-colors ${
              speed === s
                ? 'bg-amber text-white'
                : 'bg-white/10 text-white/60 hover:text-white'
            } disabled:opacity-30`}
          >
            {s}x
          </button>
        ))}
      </div>

      {/* Divider */}
      <div className="w-px h-6 bg-white/20 mx-1" />

      {/* Export as video */}
      <button
        onClick={handleExportVideo}
        disabled={recording || total === 0}
        title="Export replay as video"
        className={`flex items-center gap-1.5 px-3 h-8 rounded-lg text-xs font-display font-medium transition-colors ${
          recording
            ? 'bg-red-500/80 text-white'
            : 'bg-white/10 text-white/70 hover:text-white hover:bg-white/20'
        } disabled:opacity-50`}
      >
        {recording ? (
          <>
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            <span className="hidden sm:inline">{recordingProgress || 'Recording...'}</span>
            <span className="sm:hidden">Rec</span>
          </>
        ) : (
          <>
            <Video className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Export Video</span>
          </>
        )}
      </button>

      {/* Close */}
      <button
        onClick={handleClose}
        disabled={recording}
        className="w-8 h-8 flex items-center justify-center rounded-lg text-white/60 hover:text-white transition-colors ml-1 disabled:opacity-30"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
